import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { endOfDay, format } from 'date-fns';
import { PiGearSixDuotone } from 'react-icons/pi';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip } from 'recharts';

import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader, PopupDateRangeQuickSelector, PopupToolbar, PopupToolbarGroup, PopupBody } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';
import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';
import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';

import {
    OptimizationPipelineChartFrame,
    OptimizationPipelineChartModeToggle,
    OptimizationPipelineChartTypeToggle,
    OptimizationPipelineChartViewToggle,
    OptimizationPipelineChartTooltip,
    OptimizationPipelineChartKeySelector,
    OptimizationPipelineChartEmptyState,
    toMs,
    normalizeNumber,
    colorForIndex,
    buildDateBounds,
    buildDisabledQuickOptions,
    getChartPortalTooltipProps
} from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineChart';

const POPUP_TYPE = 'UNIT_OPTIMIZER_DETAILS_CHART';
const INITIAL_DATE_RANGE = { from: '', to: '', quickRange: 'ALL' };
const CHART_HEIGHT = 440;

function formatInputDate(date) { if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''; return format(date, 'yyyy-MM-dd'); }
function parseInputDate(value) { if (!value) return null; const date = new Date(value + 'T00:00:00'); if (Number.isNaN(date.getTime())) return null; return date; }
function formatDisplayDate(value) { const date = value instanceof Date ? value : parseInputDate(value); return date ? format(date, 'yyyy.MM.dd.') : ''; }
function getDateRangeSubtitle(dateRange) { const from = formatDisplayDate(dateRange?.from); const to = formatDisplayDate(dateRange?.to); if (from && to) return 'Showing data from ' + from + ' to ' + to; if (from) return 'Showing data from ' + from; if (to) return 'Showing data until ' + to; return 'Detailed unit optimizer parameter history'; }

function getBaseState(row) { return row?.base_state || row?.optimizer_base_state || row?.optimizerBaseState || row?.merged_data || row?.mergedData || {}; }
function getBestState(row) { return row?.best_state || row?.optimizer_best_state || row?.optimizerBestState || {}; }

function getByPath(obj, path) {
    if (!obj || typeof obj !== 'object' || !path) return undefined;

    const parts = String(path).split('.');
    let cur = obj;

    for (const part of parts) {
        if (!cur || typeof cur !== 'object') return undefined;
        if (part === 'id') return undefined;
        cur = cur[part];
    }

    return cur;
}

function flatten(obj, prefix = '', out = {}) {
    if (!obj || typeof obj !== 'object') return out;

    Object.entries(obj).forEach(([key, value]) => {
        if (key === 'id') return;

        const nextKey = prefix ? prefix + '.' + key : key;

        if (value && typeof value === 'object' && !Array.isArray(value)) flatten(value, nextKey, out);
        else out[nextKey] = value;
    });

    return out;
}

function fmtNumber(value) {
    if (value == null || Number.isNaN(value)) return '-';
    return formatLabelValue(value, defaultLabelStyle());
}

function fmtPercent(value) {
    if (value == null || Number.isNaN(value)) return '-';
    return formatLabelValue(value, { thousandsSeparator: false, decimalPlaces: 2 }) + '%';
}

function collectAvailableKeysFromLogs(rows) {
    const set = new Set();

    (Array.isArray(rows) ? rows : []).forEach((row) => {
        Object.keys(flatten(getBaseState(row))).forEach((key) => { if (key) set.add(key); });
        Object.keys(flatten(getBestState(row))).forEach((key) => { if (key) set.add(key); });
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, 'hu'));
}

function getModeValue(base, best, mode) {
    if (mode === 'base') return base;
    if (mode === 'best') return best;
    if (mode === 'pct') return base != null && best != null && base !== 0 ? ((best - base) / Math.abs(base)) * 100 : null;

    return base != null && best != null ? best - base : null;
}

function renderTableValue(row, key, mode) {
    const value = mode === 'pct' ? row[key + '__pct'] : row[key + '__' + mode];
    return mode === 'pct' ? fmtPercent(value) : fmtNumber(value);
}

export default function UnitOptimizerDetailsChartPopup() {
    const { popup, closePopup, getOnDemandResource, ensureOnDemandResource } = useOptimizationPipeline();

    const chartRef = useRef(null);

    const [isChartView, setIsChartView] = useState(true);
    const [chartType, setChartType] = useState('bar');
    const [mode, setMode] = useState('diff');
    const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [hoveredKey, setHoveredKey] = useState(null);

    const open = popup?.type === POPUP_TYPE;
    const resource = getOnDemandResource('unitOptimizerLogDataList');

    const logDataList = resource?.data;
    const loading = resource?.loading;
    const error = resource?.error;

    const title = popup?.title || 'Unit Optimizer Details';
    const defaultSelectedKeys = popup?.defaultSelectedKeys || popup?.selectedKeys || [];
    const initialMode = ['base', 'best', 'diff', 'pct'].includes(popup?.initialMode) ? popup.initialMode : 'diff';

    useEffect(() => {
        if (open) ensureOnDemandResource('unitOptimizerLogDataList');
    }, [open, ensureOnDemandResource]);

    const sortedRows = useMemo(() => {
        const list = Array.isArray(logDataList) ? logDataList : [];
        return [...list].filter((row) => row && row.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [logDataList]);

    const allKeys = useMemo(() => {
        const popupKeys = Array.isArray(popup?.availableKeys) ? popup.availableKeys.filter(Boolean) : [];
        const logKeys = collectAvailableKeysFromLogs(sortedRows);

        return Array.from(new Set([...popupKeys, ...logKeys])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'hu'));
    }, [popup?.availableKeys, sortedRows]);

    const dateBounds = useMemo(() => buildDateBounds(sortedRows), [sortedRows]);
    const disabledQuickOptions = useMemo(() => buildDisabledQuickOptions(dateBounds.minDate, dateBounds.maxDate), [dateBounds]);

    useEffect(() => {
        if (!open) return;

        setIsChartView(true);
        setChartType('bar');
        setMode(initialMode);
        setHoveredKey(null);

        if (dateBounds.minDate && dateBounds.maxDate) {
            setDateRange({
                from: formatInputDate(dateBounds.minDate),
                to: formatInputDate(dateBounds.maxDate),
                quickRange: 'ALL'
            });
        } else {
            setDateRange(INITIAL_DATE_RANGE);
        }

        const seed = Array.isArray(defaultSelectedKeys) && defaultSelectedKeys.length
            ? defaultSelectedKeys.filter(Boolean)
            : allKeys.slice(0, 1);

        setSelectedKeys(Array.from(new Set(seed)));

    }, [open, popup?.requestToken, dateBounds.minDate, dateBounds.maxDate, allKeys, initialMode]);

    const filteredData = useMemo(() => {
        if (!selectedKeys.length) return [];

        const fromDate = parseInputDate(dateRange?.from);
        const toDate = parseInputDate(dateRange?.to);

        const fromTime = fromDate ? fromDate.getTime() : -Infinity;
        const toTime = toDate ? endOfDay(toDate).getTime() : Infinity;

        const merged = {};

        for (const row of sortedRows) {
            const time = toMs(row.ts);

            if (!Number.isFinite(time) || time < fromTime || time > toTime) continue;

            const ts = row.ts;

            if (!merged[ts]) merged[ts] = { ts };

            selectedKeys.forEach((key) => {
                const baseRaw = getByPath(getBaseState(row), key);
                const bestRawDirect = getByPath(getBestState(row), key);
                const bestRaw = bestRawDirect !== undefined ? bestRawDirect : baseRaw;

                const base = normalizeNumber(baseRaw);
                const best = normalizeNumber(bestRaw);

                merged[ts][key + '__base'] = base;
                merged[ts][key + '__best'] = best;
                merged[ts][key + '__diff'] = base != null && best != null ? best - base : null;
                merged[ts][key + '__pct'] = base != null && best != null && base !== 0 ? ((best - base) / Math.abs(base)) * 100 : null;
                merged[ts][key + '__display'] = getModeValue(base, best, mode);
            });
        }

        return Object.values(merged).sort((a, b) => toMs(a.ts) - toMs(b.ts));

    }, [sortedRows, selectedKeys, dateRange, mode]);

    const isSeriesHighlighted = useCallback((key) => {
        if (!hoveredKey) return true;
        return hoveredKey === key;
    }, [hoveredKey]);

    const getSeriesOpacity = useCallback((key, highlightedOpacity = 1, mutedOpacity = 0.18) => (
        isSeriesHighlighted(key) ? highlightedOpacity : mutedOpacity
    ), [isSeriesHighlighted]);

    const getSeriesStrokeWidth = useCallback((key) => {
        if (!hoveredKey) return 2;
        return isSeriesHighlighted(key) ? 2 : 1;
    }, [hoveredKey, isSeriesHighlighted]);

    const getTooltip = useCallback(({ active, payload }) => {
        if (!(active && payload && payload.length)) return null;

        const row = payload[0]?.payload;
        if (!row) return null;

        const date = new Date(row.ts);
        const tsLabel = Number.isNaN(date.getTime()) ? String(row.ts).slice(0, 10) : format(date, 'yyyy.MM.dd.');

        return (
            <OptimizationPipelineChartTooltip title={tsLabel}>
                {selectedKeys.map((key) => {
                    const value = mode === 'pct' ? row[key + '__pct'] : row[key + '__' + mode];
                    return (
                        <div key={key} className='font-medium'>
                            {key}: {mode === 'pct' ? fmtPercent(value) : fmtNumber(value)}
                        </div>
                    );
                })}
            </OptimizationPipelineChartTooltip>
        );
    }, [selectedKeys, mode]);

    const tableConfig = useMemo(() => {
        const columns = [
            {
                key: 'ts',
                label: 'Timestamp',
                sortable: true,
                minWidth: 140,
                renderCell: ({ row }) => format(new Date(row.ts), 'yyyy.MM.dd.')
            }
        ];

        selectedKeys.forEach((key) => {
            columns.push({
                key: key + '__' + mode,
                label: key,
                sortable: true,
                minWidth: 160,
                align: 'right',
                renderCell: ({ row }) => renderTableValue(row, key, mode)
            });
        });

        return {
            columns,
            sort: { key: 'ts', direction: 'desc' }
        };
    }, [selectedKeys, mode]);

    const hasKeys = allKeys.length > 0;
    const hasFilteredData = filteredData.length > 0;

    const tooltipProps = getChartPortalTooltipProps(getTooltip, chartRef);

    return (
        <OptimizationPipelinePopup open={open} onClose={closePopup} width='1440px' overflowVisible>
            <OptimizationPipelinePopupHeader title={title} subtitle={getDateRangeSubtitle(dateRange)} icon={PiGearSixDuotone} onClose={closePopup}>
                <PopupDateRangeQuickSelector value={dateRange} onChange={setDateRange} minDate={dateBounds.minDate} maxDate={dateBounds.maxDate} disabledQuickOptions={disabledQuickOptions} />
            </OptimizationPipelinePopupHeader>

            {hasKeys && (
                <PopupToolbar className='flex-col items-stretch'>
                    <OptimizationPipelineChartKeySelector
                        options={allKeys}
                        value={selectedKeys}
                        onChange={setSelectedKeys}
                        onHoverKeyChange={setHoveredKey}
                        getColor={(key, index, selected) => colorForIndex(index, selected.length)}
                    />

                    <div className='w-full flex items-center justify-between gap-16'>
                        <PopupToolbarGroup label='Metric:'>
                            <OptimizationPipelineChartModeToggle
                                value={mode}
                                onChange={setMode}
                                options={[
                                    { value: 'base', label: 'Base' },
                                    { value: 'best', label: 'Best' },
                                    { value: 'diff', label: 'Diff' },
                                    { value: 'pct', label: 'Diff %' }
                                ]}
                            />
                        </PopupToolbarGroup>

                        <PopupToolbarGroup>
                            <OptimizationPipelineChartTypeToggle value={chartType} onChange={setChartType} disabled={!isChartView} />
                            <OptimizationPipelineChartViewToggle isChartView={isChartView} onChange={setIsChartView} />
                        </PopupToolbarGroup>
                    </div>
                </PopupToolbar>
            )}

            <PopupBody padding={false} scroll>
                <div className={isChartView ? 'p-20' : 'p-0'}>

                    {!loading && !error && hasFilteredData && (
                        isChartView ? (
                            <OptimizationPipelineChartFrame ref={chartRef} height={CHART_HEIGHT} overflowVisible>
                                {chartType === 'bar' ? (
                                    <BarChart data={filteredData}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='ts' tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} />
                                        <YAxis />
                                        <ReTooltip {...tooltipProps} />

                                        {selectedKeys.map((key, index) => (
                                            <Bar
                                                key={key}
                                                dataKey={key + '__display'}
                                                fill={colorForIndex(index, selectedKeys.length)}
                                                fillOpacity={getSeriesOpacity(key)}
                                                isAnimationActive={false}
                                            />
                                        ))}
                                    </BarChart>
                                ) : (
                                    <LineChart data={filteredData}>
                                        <CartesianGrid strokeDasharray='3 3' />
                                        <XAxis dataKey='ts' tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} />
                                        <YAxis />
                                        <ReTooltip {...tooltipProps} />

                                        {selectedKeys.map((key, index) => (
                                            <Line
                                                key={key}
                                                type='monotone'
                                                dataKey={key + '__display'}
                                                stroke={colorForIndex(index, selectedKeys.length)}
                                                strokeOpacity={getSeriesOpacity(key)}
                                                strokeWidth={getSeriesStrokeWidth(key)}
                                                dot={false}
                                                activeDot={{ r: 3 }}
                                            />
                                        ))}
                                    </LineChart>
                                )}
                            </OptimizationPipelineChartFrame>
                        ) : (
                            <OptimizationPipelinePopupTable rows={filteredData} tableConfig={tableConfig} />
                        )
                    )}

                    {loading && <OptimizationPipelineChartEmptyState>Loading optimizer details...</OptimizationPipelineChartEmptyState>}
                    {!loading && error && <OptimizationPipelineChartEmptyState>Failed to load optimizer details.</OptimizationPipelineChartEmptyState>}
                    {!loading && !hasFilteredData && <OptimizationPipelineChartEmptyState>No data available.</OptimizationPipelineChartEmptyState>}

                </div>
            </PopupBody>
        </OptimizationPipelinePopup>
    );
}
